/**
 * FilterBar Molecule
 *
 * Barra de filtros para listas - Mobile first
 */
import React, { useState } from 'react'
import { RefreshCw, Search, Filter, X } from 'lucide-react'
import Button from '../atoms/Button'
import Select from '../atoms/Select'

export default function FilterBar({
	// Props para busca
	search,
	onSearch,
	onSearchChange,
	searchPlaceholder = 'Buscar...',
	// Props para filtros
	filters = [],
	values = {},
	onChange,
	onFilterChange,
	// Outras props
	onRefresh,
	loading = false,
	className = '',
}) {
	const [showFilters, setShowFilters] = useState(false)

	// Handler unificado para mudança de filtro
	const handleFilterChange = (key, value) => {
		if (onFilterChange) {
			onFilterChange(key, value)
		} else if (onChange) {
			onChange({ ...values, [key]: value })
		}
	}

	// Handler unificado para busca
	const handleSearchChange = (e) => {
		const value = e.target.value
		if (onSearchChange) {
			onSearchChange(value)
		} else if (onSearch) {
			onSearch(value)
		}
	}

	// Contar filtros ativos
	const activeFiltersCount = Object.values(values).filter(v => v && v !== '').length

	return (
		<div className={`space-y-3 ${className}`}>
			{/* Mobile: Search + Filter Toggle */}
			<div className="flex items-center gap-2">
				{/* Search */}
				<div className="relative flex-1">
					<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-body" />
					<input
						type="text"
						value={search || ''}
						onChange={handleSearchChange}
						placeholder={searchPlaceholder}
						className="w-full pl-10 pr-4 py-2 bg-base-300 border border-base-200 rounded-lg text-text text-sm placeholder:text-text-body focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
					/>
				</div>

				{/* Filter toggle - mobile only */}
				{filters.length > 0 && (
					<button
						onClick={() => setShowFilters(!showFilters)}
						className={`
							lg:hidden p-2 rounded-lg border transition-colors relative
							${showFilters || activeFiltersCount > 0
								? 'bg-primary/10 border-primary text-primary'
								: 'bg-base-300 border-base-200 text-text-body'
							}
						`}
					>
						<Filter className="w-5 h-5" />
						{activeFiltersCount > 0 && (
							<span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-white text-xs rounded-full flex items-center justify-center">
								{activeFiltersCount}
							</span>
						)}
					</button>
				)}

				{/* Refresh */}
				{onRefresh && (
					<Button
						variant="ghost"
						size="sm"
						onClick={onRefresh}
						disabled={loading}
						className="p-2"
					>
						<RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
					</Button>
				)}
			</div>

			{/* Desktop: Filters inline */}
			{filters.length > 0 && (
				<div className="hidden lg:flex items-center gap-2 flex-wrap">
					{filters.map((filter) => (
						<Select
							key={filter.key}
							value={values[filter.key] || filter.value || ''}
							onChange={(e) => handleFilterChange(filter.key, e.target.value)}
							options={filter.options || []}
							placeholder={filter.placeholder || filter.label}
							className="w-40"
						/>
					))}
				</div>
			)}

			{/* Mobile: Filters dropdown */}
			{filters.length > 0 && showFilters && (
				<div className="lg:hidden bg-base-400 rounded-lg p-3 space-y-3 border border-base-200">
					<div className="flex items-center justify-between mb-2">
						<span className="text-sm font-medium text-text">Filtros</span>
						<button
							onClick={() => setShowFilters(false)}
							className="p-1 hover:bg-base-300 rounded"
						>
							<X className="w-4 h-4 text-text-muted" />
						</button>
					</div>
					{filters.map((filter) => (
						<div key={filter.key}>
							<label className="text-xs text-text-muted mb-1 block">
								{filter.label}
							</label>
							<Select
								value={values[filter.key] || filter.value || ''}
								onChange={(e) => handleFilterChange(filter.key, e.target.value)}
								options={filter.options || []}
								placeholder={filter.placeholder || filter.label}
								className="w-full"
							/>
						</div>
					))}
				</div>
			)}
		</div>
	)
}
